import { NavLink, useNavigate } from "react-router-dom";
import { Home, Compass, CalendarDays, User, LogOut, Users, MessageSquare, Briefcase, Sparkles, ShieldCheck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import Notifications from "@/components/Notifications";
import GlobalSearch from "@/components/GlobalSearch";
import BrandMark from "@/components/BrandMark";
import ScholarAvatar from "@/components/ScholarAvatar";

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const links = [
    { to: "/app", label: "Feed", icon: Home, end: true, tid: "nav-feed" },
    { to: "/app/discover", label: "Discover", icon: Compass, tid: "nav-discover" },
    { to: "/app/groups", label: "Groups", icon: Users, tid: "nav-groups" },
    { to: "/app/gurus", label: "Gurus", icon: Sparkles, tid: "nav-gurus" },
    { to: "/app/meetups", label: "Meetups", icon: CalendarDays, tid: "meetups-tab" },
    { to: "/app/jobs", label: "Jobs", icon: Briefcase, tid: "nav-jobs" },
    { to: "/app/chat", label: "Chat", icon: MessageSquare, tid: "nav-chat" },
    { to: "/app/profile", label: "Profile", icon: User, tid: "nav-profile" },
    ...(user?.role === "admin"
      ? [{ to: "/app/admin", label: "Admin", icon: ShieldCheck, tid: "nav-admin" }]
      : []),
  ];

  return (
    <div className="min-h-screen bg-[color:var(--ayur-cream)] text-[color:var(--ayur-ink)]">
      <header className="sticky top-0 z-30 backdrop-blur-md bg-white/80 border-b border-[color:var(--ayur-border)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-3">
          <button
            onClick={() => navigate("/app")}
            data-testid="brand-logo"
            className="flex items-center gap-2 shrink-0"
          >
            <span className="flex items-center justify-center text-[color:var(--ayur-emerald)]">
              <BrandMark size={48} />
            </span>
            <span className="font-serif-display text-2xl font-semibold text-[color:var(--ayur-emerald)] hidden sm:inline">
              VaidGram
            </span>
          </button>

          <div className="hidden lg:block flex-1 max-w-md">
            <GlobalSearch />
          </div>

          <nav className="hidden md:flex items-center gap-1 overflow-x-auto hide-scrollbar">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.end}
                data-testid={l.tid}
                className={({ isActive }) =>
                  `px-3 py-2 rounded-full text-sm font-medium transition whitespace-nowrap ${
                    isActive
                      ? "bg-[color:var(--ayur-emerald)] text-[color:var(--ayur-cream)]"
                      : "text-[color:var(--ayur-ink-2)] hover:bg-[color:var(--ayur-cream-2)]"
                  }`
                }
              >
                <span className="inline-flex items-center gap-1.5">
                  <l.icon className="w-4 h-4" /> {l.label}
                </span>
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <Notifications />
            <button
              onClick={() => navigate("/app/profile")}
              data-testid="header-avatar"
              className="flex items-center gap-1"
            >
              <ScholarAvatar user={user} size={36} />
            </button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => { logout(); navigate("/"); }}
              data-testid="logout-button"
              className="text-[color:var(--ayur-ink-2)] hover:text-[color:var(--ayur-terracotta)]"
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {children}
      </main>

      {/* Mobile bottom nav */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 z-30 backdrop-blur-md bg-white/95 border-t border-[color:var(--ayur-border)]">
        <div className="flex justify-around py-2 overflow-x-auto hide-scrollbar">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end}
              className={({ isActive }) =>
                `flex flex-col items-center gap-0.5 px-3 py-1 text-[10px] ${
                  isActive ? "text-[color:var(--ayur-emerald)]" : "text-[color:var(--ayur-mute)]"
                }`
              }
            >
              <l.icon className="w-5 h-5" />
              {l.label}
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
}
